import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { RADStoreContext } from "@cadenza/rad-react";
import { bulkPut, getAll, openDb } from "./local-store";
import { getSessionState, subscribeSession } from "./auth/session.js";

const DB_NAME = "radtool";
const STORE_NAME = "rads";

const normalizeItems = (items, previous = []) => {
  if (!Array.isArray(items)) {
    return [];
  }
  const previousMap = new Map(
    (Array.isArray(previous) ? previous : []).map((item) => [item?.id, item])
  );
  return items
    .filter((item) => item && item.id)
    .map((item) => {
      const prior = previousMap.get(item.id);
      if (prior && prior.synced && !item.synced) {
        return { ...item, synced: prior.synced, syncedAt: prior.syncedAt };
      }
      return { ...item };
    });
};

export const RADDataProvider = ({ children }) => {
  const [session, setSession] = useState(() => getSessionState());
  const [rads, setRads] = useState([]);
  const dbRef = useRef(null);
  const loadPromiseRef = useRef(null);

  useEffect(() => {
    const unsubscribe = subscribeSession(setSession);
    return unsubscribe;
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!loadPromiseRef.current) {
      loadPromiseRef.current = openDb(DB_NAME)
        .then((db) => {
          dbRef.current = db;
          return getAll(db, STORE_NAME);
        })
        .then((stored) => {
          if (!cancelled && Array.isArray(stored)) {
            setRads(stored);
          }
        })
        .catch((error) => {
          console.warn("Failed to initialise RAD local store", error);
        });
    }
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (session?.authenticated && dbRef.current) {
      getAll(dbRef.current, STORE_NAME)
        .then((stored) => {
          if (Array.isArray(stored)) {
            setRads(stored);
          }
        })
        .catch((error) => {
          console.warn("Failed to refresh RADs after session change", error);
        });
    }
  }, [session]);

  const persistLocal = useCallback(
    (nextRads, previousRads = []) => {
      const db = dbRef.current;
      if (!db) {
        return Promise.resolve();
      }
      const payload = normalizeItems(nextRads, previousRads);
      return bulkPut(db, STORE_NAME, payload).catch((error) => {
        console.error("Failed to persist RADs locally", error);
      });
    },
    []
  );

  const getRADs = useCallback(() => rads, [rads]);

  const updateRADs = useCallback(
    (nextRads) => {
      setRads((current) => {
        const snapshot = Array.isArray(nextRads) ? [...nextRads] : [];
        persistLocal(snapshot, current);
        return snapshot;
      });
    },
    [persistLocal]
  );

  const contextValue = useMemo(
    () => ({
      getRADs,
      updateRADs,
    }),
    [getRADs, updateRADs]
  );

  return <RADStoreContext.Provider value={contextValue}>{children}</RADStoreContext.Provider>;
};

export default RADDataProvider;
